// The sheet pickers (owner, 2026-09-18): the tracking model's "Sheet under
// the model", Sources' layout picker and the Align page.
//
// SEARCH across what somebody remembers a drawing by - the sheet number,
// the sheet name, the file it was uploaded as ("Install Tracker", "Look
// ahead"), its discipline and its level - with the file name and a PDF /
// DWG badge on a second line.
//
// RENDERABLE ONLY: the overlay can draw a PDF (helpers/pdfPreview) and any
// document whose pages were rendered at upload (docControl.pages - how a
// DWG or a scanned image becomes drawable). A DWG without rendered pages
// stays in the list greyed out, "can't be drawn yet"; anything else is
// left out - spreadsheets, documents, models, and images with no page
// (every one of those today is issue media: screenshots and photos).
import React from "react";
import { sheetLabel } from "./trackingTheme";
import VersionChip from "components/Documents/VersionChip";

const baseOf = (link) => String(link || "").split("?")[0].split("/").pop() || "";

export function sheetKind(s) {
  const l = baseOf(s && s.link).toLowerCase().replace(/\.gz$/, "");
  if (/\.pdf$/.test(l)) return "pdf";
  if (/\.(dwg|dxf)$/.test(l)) return "dwg";
  if (/\.(png|jpe?g|gif|webp|bmp)$/.test(l)) return "image";
  return "other";
}

const hasPages = (s) => Array.isArray(s && s.pages) && s.pages.filter(Boolean).length > 0;

// Can the overlay draw it? A DWG only through its rendered pages.
export function sheetRenderable(s) {
  if (s && s.docType === "issue-media") return false;
  const k = sheetKind(s);
  return k === "pdf" || ((k === "dwg" || k === "image") && hasPages(s));
}

export function disciplineName(s) {
  const d = s && s.discipline;
  if (!d) return String((s && s.disciplineName) || "");
  if (typeof d === "object") return String(d.value || d.name || d.key || "");
  return /^[0-9a-f]{24}$/i.test(String(d)) ? "" : String(d);
}

export const fileNameOf = (s) => String((s && s.originalFileName) || baseOf(s && s.link).replace(/\.gz$/i, "") || "");

export function sheetSearchText(s) {
  const levels = Array.isArray(s && s.floorLevel) ? s.floorLevel.join(" ") : String((s && s.floorLevel) || "");
  return [s && s.sheetNumber, s && s.sheetName, s && s.name, fileNameOf(s), disciplineName(s), levels]
    .filter(Boolean).join(" ").toLowerCase();
}

const BADGE = { pdf: ["PDF", "#B42318"], dwg: ["DWG", "#1D4ED8"], image: ["IMG", "#6B7280"] };

/**
 * Select options for a sheet list: { value, label (two lines), plain (the
 * one-line label for the closed select), search, disabled }. Only drawable
 * documents, plus undrawable DWGs greyed out.
 */
export function sheetSelectOptions(sheets, { labelOf = sheetLabel } = {}) {
  return (Array.isArray(sheets) ? sheets : [])
    .filter((s) => s && (sheetRenderable(s) || sheetKind(s) === "dwg"))
    .map((s) => {
      const kind = sheetKind(s);
      const drawable = sheetRenderable(s);
      const [badge, color] = BADGE[kind] || ["DOC", "#6B7280"];
      const plain = labelOf(s);
      const file = fileNameOf(s);
      const disc = disciplineName(s);
      return {
        value: String(s._id),
        plain,
        search: sheetSearchText(s),
        disabled: !drawable,
        label: (
          <div style={{ lineHeight: 1.25, padding: "1px 0" }} title={drawable ? file : `${file} - can't be drawn yet (no rendered pages)`}>
            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{plain}</div>
            <div style={{ fontSize: 11, color: "#8D8D8D", display: "flex", gap: 6, alignItems: "center", overflow: "hidden" }}>
              <span style={{ fontSize: 9.5, fontWeight: 700, color: "#fff", background: color, borderRadius: 3, padding: "0 4px", flexShrink: 0 }}>{badge}</span>
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {file}{disc ? ` · ${disc}` : ""}{drawable ? "" : " · can't be drawn yet"}
              </span>
              <VersionChip docId={s._id} rev={s.revisionNumber} count={(s.revisionNumber || 0) + 1} />
            </div>
          </div>
        ),
      };
    });
}

// For <Select filterOption>: every word typed must appear somewhere.
export const filterSheetOption = (input, option) => {
  const hay = (option && option.search) || "";
  return String(input || "").toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
};

// The props a sheet <Select> needs, in one place.
export const sheetSelectProps = (sheets, opts) => ({
  showSearch: true,
  options: sheetSelectOptions(sheets, opts),
  filterOption: filterSheetOption,
  optionLabelProp: "plain",
  listHeight: 360,
});
